import React, { useState, useRef, useEffect } from "react";
import {
  FaTimes,
  FaPaperPlane,
  FaUser,
  FaPhoneAlt,
  FaWhatsapp,
} from "react-icons/fa";
import { RiRobot2Fill } from "react-icons/ri";
import { GoMoveToTop } from "react-icons/go";
import { Api } from "../api/Api";

const ChatBot = () => {
  const { http } = Api();

  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTopButton, setShowTopButton] = useState(false);

  const chatHistoryRef = useRef(null);

  const supportImage =
    "https://i.pinimg.com/736x/c1/f6/3a/c1f63a105cd09ac494907045aa30d4f7.jpg";

  // =========================================================
  // AUTO SCROLL CHAT
  // =========================================================

  useEffect(() => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTop =
        chatHistoryRef.current.scrollHeight;
    }
  }, [chatHistory, loading]);

  // =========================================================
  // BACK TO TOP VISIBILITY
  // =========================================================

  useEffect(() => {
    const handleScroll = () => {
      setShowTopButton(window.scrollY > 400);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // =========================================================
  // TOGGLE CHAT
  // =========================================================

  const toggleChat = () => {
    setIsOpen((prev) => !prev);
  };

  // =========================================================
  // BACK TO TOP
  // =========================================================

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =========================================================
  // SEND MESSAGE
  // =========================================================

  const sendMessage = () => {
    const query = message.trim();

    if (!query || loading) {
      return;
    }

    const userMessage = {
      sender: "user",
      message: query,
    };

    setChatHistory((prev) => [...prev, userMessage]);

    setMessage("");

    generateResponse(query);
  };

  // =========================================================
  // ENTER KEY
  // =========================================================

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      sendMessage();
    }
  };

  // =========================================================
  // BOT RESPONSE
  // =========================================================

  const generateResponse = (query) => {
    setLoading(true);

    const formData = {
      userResponse: query,
    };

    http
      .post("/get-bot-response", formData)
      .then((res) => {
        if (res.data.status === "422") {
          setLoading(false);
          return;
        }

        if (res.data.bot_response !== null) {
          const botMessage = {
            sender: "bot",
            message: res.data.bot_response.answar,
          };

          setChatHistory((prev) => [...prev, botMessage]);
        } else {
          const botMessage = {
            sender: "bot",
            message: `
              <p>
                Sorry, I couldn't find information on that topic.
                Please contact us by
                <a
                  href="https://wa.me/8801944003077"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  WhatsApp
                </a>.
              </p>
            `,
          };

          setChatHistory((prev) => [...prev, botMessage]);
        }

        setLoading(false);
      })
      .catch((error) => {
        console.error("Chatbot Error:", error);

        const errorMessage = {
          sender: "bot",
          message:
            "<p>Sorry, something went wrong. Please try again later.</p>",
        };

        setChatHistory((prev) => [...prev, errorMessage]);

        setLoading(false);
      });
  };

  return (
    <>
      {/* =====================================================
          CHAT BOX
      ====================================================== */}

      {isOpen && (
        <div className="fbt-chatbox">

          {/* CLOSE BUTTON */}

          <button
            type="button"
            className="fbt-close-btn"
            onClick={toggleChat}
            aria-label="Close chat"
            title="Close"
          >
            <FaTimes />
          </button>

          {/* HEADER */}

          <div className="fbt-chat-header">

            <div className="fbt-support-image">
              <img
                src={supportImage}
                alt="Foot Balance Technology Support"
              />
            </div>

            <div className="fbt-support-title">
              Foot Balance Technology Customer Support
            </div>

          </div>

          {/* CHAT HISTORY */}

          <div
            className="fbt-chat-history"
            ref={chatHistoryRef}
          >
            {chatHistory.map((chat, index) => (
              <div
                key={index}
                className={`fbt-message ${
                  chat.sender === "user"
                    ? "fbt-user-message"
                    : "fbt-bot-message"
                }`}
              >

                {/* BOT AVATAR */}

                {chat.sender === "bot" && (
                  <div className="fbt-avatar">
                    <img
                      src={supportImage}
                      alt="Support"
                    />
                  </div>
                )}

                {/* MESSAGE */}

                <div className="fbt-message-content">
                  <span
                    dangerouslySetInnerHTML={{
                      __html: chat.message,
                    }}
                  />
                </div>

                {/* USER ICON */}

                {chat.sender === "user" && (
                  <div className="fbt-user-icon">
                    <FaUser />
                  </div>
                )}

              </div>
            ))}

            {/* LOADING */}

            {loading && (
              <div className="fbt-loading">
                <span></span>
                <span></span>
                <span></span>
                <span></span>
              </div>
            )}

          </div>

          {/* INPUT */}

          <div className="fbt-chat-input">

            <input
              type="text"
              placeholder="Type your message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
            />

            <button
              type="button"
              className="fbt-send-btn"
              onClick={sendMessage}
              disabled={loading || !message.trim()}
              aria-label="Send message"
              title="Send"
            >
              <FaPaperPlane />
            </button>

          </div>

        </div>
      )}

      {/* =====================================================
          FLOATING BUTTONS
      ====================================================== */}

      {!isOpen && (
        <div className="fbt-floating-container">

          {/* BACK TO TOP */}

          {showTopButton && (
            <button
              type="button"
              className="fbt-floating-btn fbt-top-btn"
              onClick={scrollToTop}
              aria-label="Back to top"
              title="Back to top"
            >
              <GoMoveToTop />
            </button>
          )}

          {/* CALL */}

          <a
            href="tel:+8801944003077"
            className="fbt-floating-btn fbt-call-btn"
            aria-label="Call us"
            title="Call"
          >
            <FaPhoneAlt />
          </a>

          {/* WHATSAPP */}

          <a
            href="https://wa.me/8801944003077"
            target="_blank"
            rel="noopener noreferrer"
            className="fbt-floating-btn fbt-whatsapp-btn"
            aria-label="WhatsApp"
            title="WhatsApp"
          >
            <FaWhatsapp />
          </a>

          {/* CHATBOT */}

          <button
            type="button"
            className="fbt-floating-btn fbt-chat-btn"
            onClick={toggleChat}
            aria-label="Open chatbot"
            title="Chatbot"
          >
            <RiRobot2Fill />
          </button>

        </div>
      )}

      {/* =====================================================
          CSS
      ====================================================== */}

      <style>{`

        /* =====================================================
           FLOATING CONTAINER
        ====================================================== */

        .fbt-floating-container {
          position: fixed;

          right: 24px;
          bottom: 24px;

          display: flex;
          flex-direction: column;
          align-items: center;

          gap: 12px;

          z-index: 999999;
        }


        /* =====================================================
           COMMON FLOATING BUTTON
        ====================================================== */

        .fbt-floating-btn {
          width: 50px;
          height: 50px;

          padding: 0;

          border: none;
          border-radius: 50%;

          display: flex;
          align-items: center;
          justify-content: center;

          cursor: pointer;
          text-decoration: none;

          outline: none;

          box-shadow:
            0 5px 15px rgba(0, 0, 0, 0.16);

          transition:
            transform 0.25s ease,
            box-shadow 0.25s ease,
            background 0.25s ease;
        }


        .fbt-floating-btn svg {
          font-size: 23px;
        }


        .fbt-floating-btn:hover {
          transform:
            translateY(-3px)
            scale(1.05);
        }


        /* =====================================================
           BACK TO TOP
        ====================================================== */

        .fbt-top-btn {
          background: #202020;
          color: #ffffff;
        }


        .fbt-top-btn:hover {
          background: #000000;
          color: #ffffff;

          box-shadow:
            0 10px 25px
            rgba(0, 0, 0, 0.30);
        }


        /* =====================================================
           CALL
        ====================================================== */

        .fbt-call-btn {
          background: #25d366;
          color: #ffffff;
        }


        .fbt-call-btn:hover {
          background: #20bd5a;
          color: #ffffff;

          box-shadow:
            0 10px 25px
            rgba(37, 211, 102, 0.40);
        }


        /* =====================================================
           WHATSAPP
        ====================================================== */

        .fbt-whatsapp-btn {
          background: #25d366;
          color: #ffffff;
        }


        .fbt-whatsapp-btn:hover {
          background: #20bd5a;
          color: #ffffff;

          box-shadow:
            0 10px 25px
            rgba(37, 211, 102, 0.40);
        }


        /* =====================================================
           CHATBOT
        ====================================================== */

        .fbt-chat-btn {
          background: #25d366;
          color: #ffffff;
        }


        .fbt-chat-btn:hover {
          background: #20bd5a;
          color: #ffffff;

          box-shadow:
            0 10px 25px
            rgba(37, 211, 102, 0.40);
        }


        /* =====================================================
           CALL ICON ANIMATION
        ====================================================== */

        .fbt-call-btn svg {
          animation:
            fbt-call-icon
            2.2s
            ease-in-out
            infinite;

          transform-origin: center;
        }


        @keyframes fbt-call-icon {

          0%,
          100% {
            transform:
              scale(1)
              rotate(0deg);
          }

          25% {
            transform:
              scale(1.08)
              rotate(-6deg);
          }

          50% {
            transform:
              scale(1.12)
              rotate(6deg);
          }

          75% {
            transform:
              scale(1.08)
              rotate(-3deg);
          }

        }


        /* =====================================================
           WHATSAPP ICON ANIMATION
        ====================================================== */

        .fbt-whatsapp-btn svg {
          animation:
            fbt-whatsapp-icon
            2.2s
            ease-in-out
            infinite;

          transform-origin: center;
        }


        @keyframes fbt-whatsapp-icon {

          0%,
          100% {
            transform:
              scale(1)
              rotate(0deg);
          }

          25% {
            transform:
              scale(1.08)
              rotate(-5deg);
          }

          50% {
            transform:
              scale(1.13)
              rotate(5deg);
          }

          75% {
            transform:
              scale(1.08)
              rotate(-2deg);
          }

        }


        /* =====================================================
           CHATBOT ICON ANIMATION
        ====================================================== */

        .fbt-chat-btn svg {
          animation:
            fbt-chatbot-icon
            2.2s
            ease-in-out
            infinite;

          transform-origin: center;
        }


        @keyframes fbt-chatbot-icon {

          0%,
          100% {
            transform:
              scale(1)
              translateY(0);
          }

          25% {
            transform:
              scale(1.08)
              translateY(-2px);
          }

          50% {
            transform:
              scale(1.14)
              translateY(0);
          }

          75% {
            transform:
              scale(1.08)
              translateY(-2px);
          }

        }


        /* =====================================================
           CHAT BOX
        ====================================================== */

        .fbt-chatbox {
          position: fixed;

          right: 24px;
          bottom: 24px;

          width: 350px;
          height: 540px;

          max-width:
            calc(100vw - 30px);

          max-height:
            calc(100vh - 48px);

          background: #ffffff;

          border-radius: 20px;

          overflow: hidden;

          display: flex;
          flex-direction: column;

          z-index: 999998;

          box-shadow:
            0 15px 50px
            rgba(0, 0, 0, 0.25);

          animation:
            fbt-chat-open
            0.25s
            ease;
        }


        @keyframes fbt-chat-open {

          from {
            opacity: 0;

            transform:
              translateY(20px)
              scale(0.96);
          }

          to {
            opacity: 1;

            transform:
              translateY(0)
              scale(1);
          }

        }


        /* =====================================================
           CLOSE BUTTON
        ====================================================== */

        .fbt-close-btn {
          position: absolute;

          top: 15px;
          right: 15px;

          width: 42px;
          height: 42px;

          padding: 0;

          border: none;
          border-radius: 50%;

          background: #ffffff;
          color: #333333;

          display: flex;
          align-items: center;
          justify-content: center;

          cursor: pointer;

          z-index: 20;

          box-shadow:
            0 4px 15px
            rgba(0, 0, 0, 0.12);

          transition:
            transform 0.25s ease,
            background 0.25s ease;
        }


        .fbt-close-btn:hover {
          background: #f3f3f3;

          transform:
            rotate(90deg);
        }


        .fbt-close-btn svg {
          font-size: 18px;
        }


        /* =====================================================
           CHAT HEADER
        ====================================================== */

        .fbt-chat-header {
          flex-shrink: 0;

          text-align: center;

          padding:
            14px
            55px
            13px
            55px;

          background: #ffffff;
        }


        .fbt-support-image {
          display: flex;

          justify-content: center;

          margin-bottom: 9px;
        }


        .fbt-support-image img {
          width: 72px;
          height: 72px;

          object-fit: cover;

          border-radius: 50%;

          border:
            3px solid
            #95df52;

          box-shadow:
            0 5px 15px
            rgba(149, 223, 82, 0.25);
        }


        .fbt-support-title {
          font-size: 16px;

          font-weight: 700;

          line-height: 1.35;

          color: #333333;
        }


        /* =====================================================
           CHAT HISTORY
        ====================================================== */

        .fbt-chat-history {
          flex: 1;

          min-height: 0;

          overflow-y: auto;

          padding: 15px;

          background: #f6faf3;

          scrollbar-width: thin;

          scrollbar-color:
            #b8e88a
            transparent;
        }


        .fbt-chat-history::-webkit-scrollbar {
          width: 5px;
        }


        .fbt-chat-history::-webkit-scrollbar-thumb {
          background: #b8e88a;

          border-radius: 10px;
        }


        /* =====================================================
           MESSAGE
        ====================================================== */

        .fbt-message {
          width: 100%;

          display: flex;

          align-items: flex-end;

          gap: 8px;

          margin-bottom: 14px;
        }


        .fbt-bot-message {
          justify-content: flex-start;
        }


        .fbt-user-message {
          justify-content: flex-end;
        }


        /* =====================================================
           BOT AVATAR
        ====================================================== */

        .fbt-avatar {
          width: 34px;
          height: 34px;

          flex:
            0 0 34px;
        }


        .fbt-avatar img {
          width: 34px;
          height: 34px;

          object-fit: cover;

          border-radius: 50%;

          border:
            2px solid
            #95df52;
        }


        /* =====================================================
           MESSAGE CONTENT
        ====================================================== */

        .fbt-message-content {
          max-width: 76%;

          padding:
            12px
            15px;

          font-size: 14px;

          line-height: 1.6;

          word-break: break-word;
        }


        .fbt-bot-message
        .fbt-message-content {
          background: #ffffff;

          color: #555555;

          border-radius:
            16px
            16px
            16px
            4px;

          box-shadow:
            0 3px 12px
            rgba(0, 0, 0, 0.07);
        }


        .fbt-user-message
        .fbt-message-content {
          background: #95df52;

          color: #ffffff;

          border-radius:
            16px
            16px
            4px
            16px;
        }


        .fbt-message-content p {
          margin: 0;
        }


        .fbt-message-content a {
          color: #55a719;

          font-weight: 600;
        }


        /* =====================================================
           USER ICON
        ====================================================== */

        .fbt-user-icon {
          width: 32px;
          height: 32px;

          flex:
            0 0 32px;

          border-radius: 50%;

          display: flex;

          align-items: center;

          justify-content: center;

          background: #eeeeee;

          color: #666666;
        }


        /* =====================================================
           LOADING
        ====================================================== */

        .fbt-loading {
          display: flex;

          align-items: center;

          gap: 5px;

          padding:
            8px
            0
            8px
            43px;
        }


        .fbt-loading span {
          width: 7px;
          height: 7px;

          border-radius: 50%;

          background: #95df52;

          animation:
            fbt-loading-animation
            1.2s
            infinite
            ease-in-out;
        }


        .fbt-loading span:nth-child(2) {
          animation-delay: 0.15s;
        }


        .fbt-loading span:nth-child(3) {
          animation-delay: 0.30s;
        }


        .fbt-loading span:nth-child(4) {
          animation-delay: 0.45s;
        }


        @keyframes fbt-loading-animation {

          0%,
          60%,
          100% {
            transform:
              translateY(0);

            opacity: 0.5;
          }

          30% {
            transform:
              translateY(-5px);

            opacity: 1;
          }

        }


        /* =====================================================
           CHAT INPUT
        ====================================================== */

        .fbt-chat-input {
          flex-shrink: 0;

          display: flex;

          align-items: center;

          gap: 8px;

          padding: 10px;

          background: #ffffff;

          border-top:
            1px solid
            #eeeeee;
        }


        .fbt-chat-input input {
          flex: 1;

          min-width: 0;

          height: 46px;

          border:
            1px solid
            #dddddd;

          border-radius: 25px;

          padding:
            0
            16px;

          font-size: 14px;

          color: #333333;

          outline: none;
        }


        .fbt-chat-input input:focus {
          border-color: #95df52;

          box-shadow:
            0 0 0 3px
            rgba(149, 223, 82, 0.12);
        }


        .fbt-chat-input input:disabled {
          background: #f5f5f5;
        }


        /* =====================================================
           SEND BUTTON
        ====================================================== */

        .fbt-send-btn {
          width: 46px;
          height: 46px;

          flex:
            0 0 46px;

          padding: 0;

          border: none;
          border-radius: 50%;

          display: flex;

          align-items: center;

          justify-content: center;

          background: #95df52;

          color: #ffffff;

          cursor: pointer;

          transition:
            transform 0.2s ease,
            background 0.2s ease;
        }


        .fbt-send-btn:hover:not(:disabled) {
          background: #82cf3e;

          transform:
            scale(1.05);
        }


        .fbt-send-btn:disabled {
          opacity: 0.5;

          cursor: not-allowed;
        }


        .fbt-send-btn svg {
          font-size: 18px;
        }


        /* =====================================================
           TABLET
        ====================================================== */

        @media (max-width: 768px) {

          .fbt-floating-container {
            right: 15px;

            bottom: 15px;

            gap: 10px;
          }


          .fbt-floating-btn {
            width: 50px;
            height: 50px;
          }


          .fbt-floating-btn svg {
            font-size: 21px;
          }


          .fbt-chatbox {
            right: 15px;

            bottom: 15px;

            width:
              calc(100vw - 30px);

            height:
              calc(100vh - 30px);

            max-height:
              calc(100vh - 30px);

            border-radius: 18px;
          }


          .fbt-support-title {
            font-size: 15px;
          }

        }


        /* =====================================================
           SMALL MOBILE
        ====================================================== */

        @media (max-width: 480px) {

          .fbt-floating-container {
            right: 12px;

            bottom: 12px;

            gap: 9px;
          }


          .fbt-floating-btn {
            width: 48px;
            height: 48px;
          }


          .fbt-floating-btn svg {
            font-size: 20px;
          }


          .fbt-chatbox {
            right: 12px;

            bottom: 12px;

            width:
              calc(100vw - 24px);

            height:
              calc(100vh - 24px);

            max-width:
              calc(100vw - 24px);

            max-height:
              calc(100vh - 24px);

            border-radius: 18px;
          }


          .fbt-chat-header {
            padding:
              12px
              50px
              10px
              50px;
          }


          .fbt-support-image img {
            width: 62px;
            height: 62px;
          }


          .fbt-support-title {
            font-size: 14px;
          }


          .fbt-message-content {
            max-width: 78%;

            font-size: 13px;
          }

        }


        /* =====================================================
           REDUCE ANIMATION
        ====================================================== */

        @media (prefers-reduced-motion: reduce) {

          .fbt-call-btn svg,
          .fbt-whatsapp-btn svg,
          .fbt-chat-btn svg {
            animation: none;
          }

          .fbt-chatbox {
            animation: none;
          }

        }

      `}</style>
    </>
  );
};

export default ChatBot;
